import Header from "@/components/Header";
import Footer from "@/components/Footer";

const SECTIONS = [
  ["Listing URL verification",
    "Whenever a listing includes a public source URL, our research team opens it and confirms that the advertised screening criteria match what was submitted — rent, minimum credit score, income requirement, reserves, application fee, and cosigner policy. If the link is broken or the details do not match, the entry is not marked Verified."],
  ["MLS and professional-source verification",
    "For listings sourced from the MLS or other professional listing platforms, we confirm the property and its advertised requirements against that source. Professionally listed properties give us a reliable, checkable record of the criteria a landlord or agent advertised."],
  ["Duplicate and scope checks",
    "Before an entry counts toward any statistic, we check for duplicates by source, location, and property details so no property is counted twice. Entries that fall outside California or outside our scope are excluded."],
  ["How we treat participant stories",
    "Stories are reviewed to confirm they are genuine, good-faith submissions from real participants — not spam, duplicates, or fabricated entries. We do not, and cannot, verify the factual truth of what happened in any individual experience. A submitted story is a first-hand account, not a finding of fact by the Project."],
  ["Verified vs. unverified data",
    "Only entries marked Verified by a researcher contribute to published aggregate statistics and the Annual Report. Pending, rejected, and archived entries are held separately and never appear in public figures."],
  ["Why this matters",
    "This separation between what is independently verified and what is self-reported is central to our credibility. It lets us publish confident, checkable statistics about advertised screening criteria while treating personal experiences with appropriate care and humility."],
];

export default function ResearchStandardsPage() {
  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-calblue">Research Standards</div>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
          What we verify — and what we don't.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          Every figure we publish rests on a clear standard of evidence. This page explains how
          we verify advertised screening criteria, how we review participant stories, and why we
          are careful to distinguish independently verified data from self-reported experience.
        </p>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-32 md:px-12">
        <div className="space-y-px bg-slate-200">
          {SECTIONS.map(([title, body], i) => (
            <div key={title} data-testid={`standard-${i + 1}`} className="bg-white py-8">
              <div className="flex gap-6">
                <span className="font-mono text-sm text-calblue">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-serif text-2xl font-medium leading-snug text-ink">{title}</h2>
                  <p className="mt-3 leading-relaxed text-slate-600">{body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 border-l-2 border-navy pl-6">
          <p className="max-w-2xl font-serif text-2xl font-normal leading-snug text-ink">
            We verify the criteria a listing advertised. We confirm a story is genuine. We never
            claim to judge whose account is true.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
